import { useEffect } from "react";
import { motion } from "framer-motion";
import { platformMeta, socialMeta } from "@/constants/platforms";
import { fileUrl } from "@/lib/api";
import { formatDateBadge, formatLocalTime, formatPrice } from "@/lib/eventTime";
import { loadHeadlineFont } from "@/lib/loadHeadlineFont";

const HEADER_MOTION_PROPS = {
  initial: { opacity: 0, y: -14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const SOCIAL_ROW_MOTION_PROPS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.5 },
};

function backgroundStyleFor(page) {
  const { colors, background_style } = page;
  if (background_style === "solid") {
    return { backgroundColor: colors.background };
  }
  if (background_style === "blurred_cover" && page.cover_image_url) {
    return {
      backgroundColor: colors.background,
      backgroundImage: `linear-gradient(180deg, ${colors.background}cc, ${colors.background}f5), url(${fileUrl(page.cover_image_url)})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return {
    backgroundImage: `radial-gradient(circle at 50% -10%, ${colors.accent}33, transparent 55%), linear-gradient(180deg, ${colors.background}, #05060a)`,
    backgroundColor: colors.background,
  };
}

export function FanPageView({ page, onLinkClick, scale = "full" }) {
  const isEvent = page.type === "event";
  const colors = page.colors || { background: "#0D0914", accent: "#EC4899", text: "#FFFFFF" };
  const containerPad = scale === "full" ? "p-5 sm:p-8" : "p-4";
  const headlineStyle = page.headline_font ? { fontFamily: `"${page.headline_font}", sans-serif` } : undefined;

  useEffect(() => {
    loadHeadlineFont(page.headline_font);
  }, [page.headline_font]);

  return (
    <div
      className={`min-h-full w-full transition-colors duration-700 ${scale === "full" ? "min-h-screen" : ""}`}
      style={{ ...backgroundStyleFor(page), color: colors.text }}
      data-testid="fan-page-view"
    >
      <div className={`mx-auto max-w-md md:max-w-xl ${containerPad} space-y-6`}>
        <motion.div
          {...HEADER_MOTION_PROPS}
          className="flex flex-col items-center text-center space-y-4 pt-4"
        >
          {page.cover_image_url ? (
            <div
              className="w-full max-h-[420px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 flex items-center justify-center bg-black/20"
              style={!isEvent ? { boxShadow: `0 20px 60px -10px ${colors.accent}55` } : undefined}
            >
              <img
                src={fileUrl(page.cover_image_url)}
                alt={isEvent ? page.event_name || page.title : page.title}
                className="max-w-full max-h-[420px] w-auto h-auto object-contain"
                data-testid={isEvent ? "fan-page-banner" : "fan-page-cover"}
              />
            </div>
          ) : (
            <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              <i className={isEvent ? "fa-solid fa-calendar-days text-3xl opacity-40" : "fa-solid fa-record-vinyl text-3xl opacity-40"} />
            </div>
          )}

          <div className="space-y-1.5">
            <h1
              className={`font-black leading-tight tracking-tight ${isEvent ? "font-event uppercase text-xl sm:text-2xl" : "font-display text-2xl sm:text-3xl"}`}
              style={headlineStyle}
              data-testid="fan-page-title"
            >
              {isEvent ? page.event_name || "Untitled Event" : page.title || "Untitled Release"}
            </h1>
            {!isEvent && page.artist_name && (
              <p className="text-sm sm:text-base opacity-80 font-medium" data-testid="fan-page-artist">{page.artist_name}</p>
            )}
            {page.description && (
              <p className="text-sm opacity-75 max-w-sm mx-auto pt-1" data-testid="fan-page-description">{page.description}</p>
            )}
          </div>
        </motion.div>

        <div className="space-y-3">
          {!isEvent &&
            [...(page.platform_links || [])].sort((a, b) => a.order - b.order).map((link, i) => {
              const meta = platformMeta(link.platform);
              return (
                <motion.a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onLinkClick && onLinkClick(link.id, meta.label)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.07 }}
                  className="group flex items-center gap-4 w-full rounded-full px-5 py-3.5 bg-white/[0.07] backdrop-blur-md border border-white/10 hover:-translate-y-0.5 hover:border-white/25 transition-all duration-200 active:scale-95"
                  style={{ boxShadow: `0 0 0 0 transparent` }}
                  data-testid={`fan-link-button-${link.platform}`}
                >
                  <span
                    className="flex items-center justify-center w-9 h-9 rounded-full shrink-0 text-white"
                    style={{ backgroundColor: meta.color }}
                  >
                    <i className={`${meta.icon} text-base`} />
                  </span>
                  <span className="flex-1 text-left font-semibold text-sm sm:text-base">{meta.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0 text-xs sm:text-sm font-bold opacity-80 group-hover:opacity-100">
                    {link.cta_label || "Play"}
                    <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </motion.a>
              );
            })}

          {isEvent &&
            [...(page.tour_stops || [])].sort((a, b) => a.order - b.order).map((stop, i) => {
              const badge = formatDateBadge(stop);
              const localTime = formatLocalTime(stop.start_datetime_utc);
              const priceLabel = formatPrice(stop.currency, stop.price);
              return (
                <motion.div
                  key={stop.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.07 }}
                  className="flex items-center gap-4 w-full rounded-2xl px-4 py-3.5 bg-white/[0.07] backdrop-blur-md border border-white/10"
                  data-testid={`fan-page-tour-stop-${stop.id}`}
                >
                  <div
                    className="flex flex-col items-center justify-center w-14 h-14 rounded-xl shrink-0 font-bold"
                    style={{ backgroundColor: `${colors.accent}2a`, color: colors.accent }}
                  >
                    <span className="text-[10px] uppercase leading-none">{badge.month}</span>
                    <span className="text-base leading-none mt-0.5">{badge.day}</span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold text-sm sm:text-base truncate">{stop.city || "City TBA"}</p>
                    <p className="text-xs opacity-60 truncate">{stop.venue || "Venue TBA"}</p>
                    <p className="text-xs opacity-50 truncate" data-testid={`fan-page-tour-stop-meta-${stop.id}`}>
                      {[localTime && `${localTime} your time`, priceLabel && `From ${priceLabel}`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {stop.ticket_url ? (
                    <a
                      href={stop.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onLinkClick && onLinkClick(stop.id, `${stop.city} tickets`, stop.cta_label)}
                      className="shrink-0 rounded-full px-4 py-2 text-xs sm:text-sm font-bold whitespace-nowrap hover:-translate-y-0.5 transition-all active:scale-95"
                      style={{ backgroundColor: colors.accent, color: "#0B0E14" }}
                      data-testid={`fan-page-ticket-link-${stop.id}`}
                    >
                      {stop.cta_label || "Get Tickets"}
                    </a>
                  ) : (
                    <span className="shrink-0 rounded-full px-4 py-2 text-xs opacity-40">Soon</span>
                  )}
                </motion.div>
              );
            })}
        </div>

        {page.social_links && page.social_links.length > 0 && (
          <motion.div
            {...SOCIAL_ROW_MOTION_PROPS}
            className="flex justify-center gap-5 pt-2"
          >
            {page.social_links.map((s, i) => {
              const meta = socialMeta(s.platform);
              return (
                <a
                  key={s.id ?? i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg opacity-70 hover:opacity-100 transition-opacity"
                  data-testid={`fan-page-social-${s.platform}`}
                >
                  <i className={meta.icon} />
                </a>
              );
            })}
          </motion.div>
        )}

        <p className="text-center text-[11px] opacity-30 pt-4 pb-6 font-mono-slug">powered by SmartLinks</p>
      </div>
    </div>
  );
}
